import { useCallback, useLayoutEffect, useMemo } from 'react'
import { MenuIVRPanelProps } from './MenuIVRPanel.component'

export const MENU_IVR_DIAL_PAD_KEYS = [
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '*',
  '0',
  '#',
] as const

export type MenuIvrActionValue =
  | 'call_ext'
  | 'collect_info'
  | 'round_robin'
  | 'go_to_menu'
  | 'ai_receptionist'

export const MENU_IVR_ACTION_OPTIONS: {
  label: string
  value: MenuIvrActionValue
}[] = [
  { label: 'Call ext.', value: 'call_ext' },
  // { label: 'Collect info', value: 'collect_info' }, // TODO: Add back in when we have a plan on collect info flow
  { label: 'Round robin', value: 'round_robin' },
  { label: 'Go to menu', value: 'go_to_menu' },
  { label: 'AI receptionist', value: 'ai_receptionist' },
]

export const MENU_IVR_COLLECT_INPUT_OPTIONS = [
  { label: 'Patient name', value: 'patient_name' },
  { label: 'Date of birth', value: 'dob' },
  { label: 'Callback number', value: 'callback' },
]

const DEFAULT_META: Record<string, string> = {
  extension: '',
  input: '',
  team: '',
  submenu: '',
}

const KEYPAD_INDEX: Record<string, number> = {}
MENU_IVR_DIAL_PAD_KEYS.forEach((k, i) => {
  KEYPAD_INDEX[k] = i
})

export const getDefaultMenuIvrKeys = (): {
  digit: string
  action: string
  meta?: Record<string, string>
}[] => [{ digit: '0', action: 'go_to_menu', meta: { ...DEFAULT_META } }]

const isKnownAction = (a: string): a is MenuIvrActionValue =>
  MENU_IVR_ACTION_OPTIONS.some((o) => o.value === a)

const normalizeKeyRow = (row: {
  digit: string
  action: string
  meta?: Record<string, string>
}): {
  digit: string
  action: MenuIvrActionValue
  meta: Record<string, string>
} => {
  const digit = (row.digit || '').trim()
  const rawAction = (row.action || '').trim()
  if (isKnownAction(rawAction)) {
    return {
      digit,
      action: rawAction,
      meta: { ...DEFAULT_META, ...row.meta },
    }
  }
  if (rawAction) {
    return {
      digit,
      action: 'call_ext',
      meta: { ...DEFAULT_META, ...row.meta, legacyLabel: rawAction },
    }
  }
  return {
    digit,
    action: 'call_ext',
    meta: { ...DEFAULT_META, ...row.meta },
  }
}

const sortByDialOrder = <T extends { digit: string }>(rows: T[]): T[] =>
  [...rows].sort((a, b): number => {
    const ia = a.digit in KEYPAD_INDEX ? KEYPAD_INDEX[a.digit]! : 99
    const ib = b.digit in KEYPAD_INDEX ? KEYPAD_INDEX[b.digit]! : 100
    return ia - ib
  })

const rowToStep = (row: {
  digit: string
  action: MenuIvrActionValue
  meta: Record<string, string>
}): { digit: string; action: string; meta?: Record<string, string> } => {
  const meta: Record<string, string> = { ...row.meta }
  if (row.action !== 'call_ext' && 'legacyLabel' in meta) {
    delete meta.legacyLabel
  }
  return {
    digit: row.digit,
    action: row.action,
    ...(Object.keys(meta).length > 0 ? { meta } : {}),
  }
}

interface IMenuIVRHooks {
  addKeyForDigit: (d: (typeof MENU_IVR_DIAL_PAD_KEYS)[number]) => void
  addKeyRow: () => void
  digitOptionsForRow: (index: number) => { label: string; value: string }[]
  isDigitAssigned: (d: string) => boolean
  keyRows: {
    digit: string
    action: MenuIvrActionValue
    meta: Record<string, string>
  }[]
  removeKeyRow: (index: number) => void
  updateKeyRow: (
    index: number,
    data: Partial<{
      digit: string
      action: MenuIvrActionValue
      meta: Record<string, string>
    }>,
  ) => void
}

const useMenuIVR = (props: MenuIVRPanelProps): IMenuIVRHooks => {
  const { step, onChange } = props

  useLayoutEffect(() => {
    if (Array.isArray(step.keys)) return
    onChange({ keys: getDefaultMenuIvrKeys() })
  }, [step.id, step.keys, onChange])

  const keyRows = useMemo(() => {
    const k = step.keys ?? []
    if (k.length === 0) return []
    return sortByDialOrder(k).map((row) => normalizeKeyRow(row))
  }, [step.keys])

  const commitRows = useCallback(
    (next: IMenuIVRHooks['keyRows']) => {
      onChange({
        keys: next.map((r) => rowToStep(r)),
      })
    },
    [onChange],
  )

  const updateKeyRow: IMenuIVRHooks['updateKeyRow'] = useCallback(
    (index, data) => {
      const next = keyRows.map((row, i) => {
        if (i !== index) return { ...row }
        const meta = { ...row.meta, ...data.meta }
        if (data.action && data.action !== row.action) {
          Object.assign(meta, DEFAULT_META)
        }
        return {
          digit: data.digit !== undefined ? data.digit : row.digit,
          action: data.action ?? row.action,
          meta,
        }
      })
      if (data.digit !== undefined) {
        const d = data.digit
        const j = next.findIndex((r, i) => i !== index && r.digit === d)
        if (j >= 0) {
          const prevD = keyRows[index]!.digit
          next[j] = { ...next[j]!, digit: prevD }
        }
      }
      commitRows(sortByDialOrder(next))
    },
    [keyRows, commitRows],
  )

  const addKeyRow = useCallback(() => {
    const used = new Set(keyRows.map((r) => r.digit).filter(Boolean))
    const free = MENU_IVR_DIAL_PAD_KEYS.find((d) => !used.has(d))
    if (!free) return
    commitRows(
      sortByDialOrder([
        ...keyRows,
        {
          digit: free,
          action: 'call_ext',
          meta: { ...DEFAULT_META },
        },
      ]),
    )
  }, [keyRows, commitRows])

  const addKeyForDigit = useCallback(
    (d: (typeof MENU_IVR_DIAL_PAD_KEYS)[number]) => {
      if (keyRows.some((r) => r.digit === d)) return
      commitRows(
        sortByDialOrder([
          ...keyRows,
          { digit: d, action: 'call_ext', meta: { ...DEFAULT_META } },
        ]),
      )
    },
    [keyRows, commitRows],
  )

  const removeKeyRow = useCallback(
    (index: number) => {
      const next = keyRows.filter((_, i) => i !== index)
      commitRows(next)
    },
    [keyRows, commitRows],
  )

  const digitOptionsForRow = useCallback(
    (index: number) => {
      const current = keyRows[index]?.digit
      return MENU_IVR_DIAL_PAD_KEYS.map((d) => ({
        label: d,
        value: d,
      })).filter(
        (opt) =>
          opt.value === current ||
          !keyRows.some((r, i) => i !== index && r.digit === opt.value),
      )
    },
    [keyRows],
  )

  const isDigitAssigned = useCallback(
    (d: string) => keyRows.some((r) => r.digit === d),
    [keyRows],
  )

  return {
    addKeyForDigit,
    addKeyRow,
    digitOptionsForRow,
    isDigitAssigned,
    keyRows,
    removeKeyRow,
    updateKeyRow,
  }
}

export default useMenuIVR
